import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Flame } from "lucide-react";
import { modeColor } from "@/lib/gamemode";

const SPEED = 14; // px per second

export default function ActivityTickerBar() {
  const [items, setItems] = useState([]);
  const [newKeys, setNewKeys] = useState(() => new Set());
  const sigRef = useRef("");
  const prevKeysRef = useRef(null);
  const glowTimerRef = useRef(null);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const halfRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/public/activity?limit=20");
        if (!alive) return;
        // Only swap the list when the content actually changed (order-insensitive).
        const sig = (data || []).map((d) => `${d.rsn}|${d.title}|${d.points}`).sort().join("~");
        if (sig !== sigRef.current) {
          sigRef.current = sig;
          const keys = new Set((data || []).map((d) => `${d.rsn}|${d.title}|${d.points}`));
          // Glow only entries that are genuinely new since the last poll (never on first load).
          if (prevKeysRef.current) {
            const fresh = new Set([...keys].filter((k) => !prevKeysRef.current.has(k)));
            if (fresh.size) {
              setNewKeys(fresh);
              clearTimeout(glowTimerRef.current);
              glowTimerRef.current = setTimeout(() => setNewKeys(new Set()), 6000);
            }
          }
          prevKeysRef.current = keys;
          setItems(data);
        }
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); clearTimeout(glowTimerRef.current); };
  }, []);

  // requestAnimationFrame-driven scroll: exactly ONE loop for the component's lifetime (keyed on
  // hasItems so content updates never spawn a second loop → no acceleration). Pauses automatically
  // in background tabs (so it never "catches up"/skips on return) and rounds to whole pixels.
  const hasItems = items.length > 0;
  useEffect(() => {
    if (!hasItems) return;
    const el = trackRef.current;
    if (!el) return;
    const measure = () => { halfRef.current = el.scrollWidth / 2; };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    let raf;
    let last = performance.now();
    const tick = (now) => {
      let dt = (now - last) / 1000;
      last = now;
      if (dt > 0.05) dt = 0.05; // cap large gaps (tab-switch / stalls) → no jump
      const half = halfRef.current;
      if (!pausedRef.current && half > 0) {
        let off = offsetRef.current - SPEED * dt;
        if (-off >= half) off += half; // seamless wrap
        offsetRef.current = off;
        // Subpixel transform (GPU-composited) → buttery glide, no whole-pixel stepping/judder.
        el.style.transform = `translate3d(${off.toFixed(2)}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    const onVis = () => { if (!document.hidden) last = performance.now(); };
    document.addEventListener("visibilitychange", onVis);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [hasItems]);

  if (!hasItems) return null;

  // Repeat the unique items until one "set" is wider than any viewport, then duplicate it once
  // so wrapping by half the track width is always seamless with no gaps.
  let base = items;
  if (items.length < 12) {
    base = [];
    while (base.length < 12) base = base.concat(items);
  }
  const loop = [...base, ...base];

  return (
    <div
      className="flex items-center overflow-hidden mb-6 px-3 py-1.5 border-y-2"
      data-testid="activity-ticker"
      style={{ background: "#1b150d", borderColor: "#3a2f1c", boxShadow: "inset 0 0 24px rgba(0,0,0,0.55)" }}
    >
      <span className="flex items-center gap-1.5 pr-3 shrink-0">
        <Flame size={15} className="text-[#FF7A1A]" />
        <span className="osrs-num text-[#FFCF3F] text-xs uppercase tracking-wide hidden sm:inline">Live</span>
      </span>
      <div
        className="relative flex-1 overflow-hidden"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <div
          ref={trackRef}
          className="flex items-center whitespace-nowrap"
        >
          {loop.map((it, i) => {
            const key = `${it.rsn}|${it.title}|${it.points}`;
            const isNew = newKeys.has(key);
            return (
              <span key={i} data-testid="ticker-item" className={`inline-flex items-center gap-1.5 px-5 osrs-shadow text-sm${isNew ? " ts-ticker-new" : ""}`}>
                <span className="osrs-num font-bold" style={{ color: modeColor(it.gamemode) }}>{it.rsn}</span>
                <span className="text-[#b9a06a]">completed</span>
                <span className="text-[#7CFC7C]">{it.title}</span>
                <span className="text-[#8a7a54] text-xs">(+{it.points})</span>
                <span className="text-[#5a4a2a]">•</span>
              </span>
            );
          })}
        </div>
        {/* Static edge fades to the strip colour (blend perfectly over any scene) */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10" style={{ background: "linear-gradient(to right, #1b150d, transparent)" }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10" style={{ background: "linear-gradient(to left, #1b150d, transparent)" }} />
      </div>
    </div>
  );
}
